import React, { useCallback } from "react";
import { connect } from "react-redux";

import Tree, { DataNode } from "antd/es/tree";
import cn from "classnames";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import { areCheckInFieldsVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";

import styles from "./ColumnTree.module.scss";
import { FIELD_PREFIX } from "./util/const";
import useFieldTree from "./util/useFieldTree";

interface Props {
  categories: FieldCategory[];
  categorizedFields: Partial<Record<FieldCategory, IField[]>>;
  checkInFieldsVisibility: boolean;
  className?: string;
  fixedFields?: IField[];
  onChangeSelection: (selectedFields: IField["name"][]) => void;
  query: string;
  selectedFieldsNames: IField["name"][];
  showCategories?: boolean;
}

const ColumnTree: React.FC<Props> = ({
  categories,
  categorizedFields,
  checkInFieldsVisibility,
  className,
  fixedFields,
  onChangeSelection,
  query,
  selectedFieldsNames,
  showCategories,
}) => {
  const intl = useIntl();

  const handleCheck = useCallback(
    (_, { checked, node }: { checked: boolean; node: DataNode }) => {
      const names = node.children
        ? node.children.map(({ key }) => key.toString().replace(FIELD_PREFIX, ""))
        : [node.key.toString().replace(FIELD_PREFIX, "")];

      if (names.includes(PersonFieldName.COMPANIES)) {
        names.push(PersonFieldName.COMPANIES_IMPORT_ID);
      } else if (names.includes(CommonFieldName.COMPANY)) {
        names.push(CommonFieldName.COMPANY_IMPORT_ID);
      }

      if (names.includes(CommonFieldName.PERSON)) {
        names.push(CommonFieldName.PERSON_IMPORT_ID);
      }
      if (names.includes(ActivityFieldName.DEAL)) {
        names.push(ActivityFieldName.DEAL_IMPORT_ID);
      }
      if (names.includes(CompanyFieldName.PARENT_COMPANY)) {
        names.push(CompanyFieldName.PARENT_COMPANY_IMPORT_ID);
      }
      if (names.includes(UniversalFieldName.LAST_ACTIVITY_DATE) && checked) {
        names.push(UniversalFieldName.LAST_ACTIVITY_NAME, UniversalFieldName.LAST_ACTIVITY_TYPE);
      }

      if (names.includes(CommonFieldName.OWNER_NAME)) {
        names.push(CommonFieldName.OWNER_EMAIL);
      }

      if (checked) {
        onChangeSelection(Array.from(new Set([...names, ...selectedFieldsNames])));
      } else {
        onChangeSelection(selectedFieldsNames.filter((name) => !names.includes(name)));
      }
    },
    [onChangeSelection, selectedFieldsNames]
  );

  const [treeData, checkedKeys] = useFieldTree(
    intl,
    categories,
    categorizedFields,
    selectedFieldsNames,
    query,
    checkInFieldsVisibility,
    fixedFields,
    showCategories
  );

  return (
    <Tree
      checkable
      checkedKeys={checkedKeys}
      checkStrictly
      className={cn(className, { [styles.singleLevel]: !showCategories })}
      defaultExpandAll
      onCheck={handleCheck}
      selectable={false}
      treeData={treeData}
      virtual
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  checkInFieldsVisibility: areCheckInFieldsVisible(state),
});

export default connect(mapStateToProps)(ColumnTree);
